/* ===========================================================================
   new-product.js  —  companion behaviour for sections/new-product.liquid
   - gallery: mobile scroll-snap tracking + thumb strip + arrows,
              desktop click -> fullscreen scroll-spy viewer
   - options: colour / size -> variant (price, stock note, ATC, gallery, URL)
   - drawers: one shared right-slide panel per section, relocated to <body>
   IIFE, per-section data-np-ready guard, re-inits on shopify:section:load.
   - buy: AJAX add-to-cart -> this theme's own <new-cart-drawer> (assets/new-cart.js)
   =========================================================================== */
(function () {
  'use strict';

  /* ---------- money (standard Shopify formatMoney) ---------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = format || '${{amount}}';
    function d(v, def) { return (v == null || v !== v) ? def : v; }
    function withDelims(number, precision, thousands, decimal) {
      precision = d(precision, 2); thousands = d(thousands, ','); decimal = d(decimal, '.');
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var frac = parts[1] ? decimal + parts[1] : '';
      return whole + frac;
    }
    var match = fmt.match(placeholder);
    var key = match ? match[1] : 'amount';
    var value;
    switch (key) {
      case 'amount': value = withDelims(cents, 2); break;
      case 'amount_no_decimals': value = withDelims(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelims(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelims(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelims(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelims(cents, 0, ' ', ''); break;
      case 'amount_with_apostrophe_separator': value = withDelims(cents, 2, "'", '.'); break;
      default: value = withDelims(cents, 2);
    }
    value = value.replace(/([.,])0+$/, ''); // match Liquid money_without_trailing_zeros
    return fmt.replace(placeholder, value);
  }

  function readJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  function each(list, fn) { Array.prototype.forEach.call(list, fn); }

  /* ---------- scroll lock — block wheel/touch/keys, no CSS side effects ----------
     Setting overflow:hidden on <html> breaks position:sticky (the rail jumps).
     Instead we swallow the scroll events while a panel is open. The page never
     moves, the scrollbar stays, sticky keeps working. Panel content still
     scrolls (its container is whitelisted + has overscroll-behavior: contain). */
  var npLockCount = 0;
  var NP_SCROLLABLE = '.np-drawer__body, .np-viewer__scroll, .np-viewer__rail';
  var NP_SCROLL_KEYS = { ' ': 1, 'PageUp': 1, 'PageDown': 1, 'Home': 1, 'End': 1, 'ArrowUp': 1, 'ArrowDown': 1, 'ArrowLeft': 1, 'ArrowRight': 1 };
  function npBlockWheel(e) {
    if (e.target && e.target.closest && e.target.closest(NP_SCROLLABLE)) return;
    e.preventDefault();
  }
  function npBlockKeys(e) {
    if (!NP_SCROLL_KEYS[e.key]) return;
    var a = document.activeElement;
    if (a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.isContentEditable)) return;
    if (e.target && e.target.closest && e.target.closest(NP_SCROLLABLE)) return;
    e.preventDefault();
  }
  var npScrollY = 0;
  function lockScroll() {
    npLockCount++;
    if (npLockCount > 1) return;
    npScrollY = window.scrollY;
    // no body/html freeze here on purpose — position:fixed on <body> knocks
    // any position:sticky descendant (the gallery rail) out of its stuck
    // offset and snaps it to a different spot the instant the drawer opens.
    // Blocking the scroll-causing events is enough on its own and leaves
    // sticky elements alone.
    window.addEventListener('wheel', npBlockWheel, { passive: false });
    window.addEventListener('touchmove', npBlockWheel, { passive: false });
    window.addEventListener('keydown', npBlockKeys, true);
  }
  function unlockScroll() {
    npLockCount = Math.max(0, npLockCount - 1);
    if (npLockCount > 0) return;
    window.removeEventListener('wheel', npBlockWheel, { passive: false });
    window.removeEventListener('touchmove', npBlockWheel, { passive: false });
    window.removeEventListener('keydown', npBlockKeys, true);
  }

  /* ======================================================================
     GALLERY
     ====================================================================== */
  function initGallery(root, cfg) {
    var grid = root.querySelector('[data-np-gallery]');
    if (!grid) return;
    var cells = Array.prototype.slice.call(grid.querySelectorAll('[data-np-slide]'));
    if (!cells.length) return;
    var thumbsTrack = root.querySelector('[data-np-thumbs]');
    var thumbs = thumbsTrack ? Array.prototype.slice.call(thumbsTrack.querySelectorAll('[data-np-thumb]')) : [];
    var prevBtn = root.querySelector('[data-np-thumb-prev]');
    var nextBtn = root.querySelector('[data-np-thumb-next]');
    var mq = window.matchMedia('(max-width: 749px)');
    var lightboxOn = cfg.enableLightbox !== false;

    function cellW() { return cells[0].getBoundingClientRect().width || 1; }

    function markThumb(i) {
      each(thumbs, function (t, n) { t.classList.toggle('is-active', n === i); });
      var t = thumbs[i];
      if (t && thumbsTrack) {
        var left = t.offsetLeft;
        var vis = left >= thumbsTrack.scrollLeft && left + t.offsetWidth <= thumbsTrack.scrollLeft + thumbsTrack.clientWidth;
        if (!vis) thumbsTrack.scrollTo({ left: left - thumbsTrack.clientWidth / 2 + t.offsetWidth / 2, behavior: 'smooth' });
      }
    }

    /* ---- MOBILE (≤749px): native horizontal scroll-snap on .np-gallery__grid.
       JS only mirrors the active thumbnail and answers programmatic jumps.
       Desktop keeps the CSS grid and never runs this. ---- */
    function goToIndex(i, instant) {
      i = Math.max(0, Math.min(cells.length - 1, i));
      if (mq.matches) {
        grid.scrollTo({ left: i * cellW(), behavior: instant ? 'auto' : 'smooth' });
      } else if (cells[i]) {
        cells[i].scrollIntoView({ behavior: instant ? 'auto' : 'smooth', block: 'center' });
      }
      markThumb(i);
    }

    var scrollRaf;
    grid.addEventListener('scroll', function () {
      if (!mq.matches) return;
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      scrollRaf = requestAnimationFrame(function () {
        markThumb(Math.round(grid.scrollLeft / cellW()));
      });
    }, { passive: true });

    each(thumbs, function (t, i) {
      t.addEventListener('click', function () {
        if (mq.matches) goToIndex(i);
        else if (lightboxOn && t.getAttribute('data-np-media-type') === 'image') openViewer(i);
      });
    });

    /* arrows step the main image one at a time; goToIndex clamps at the ends
       and markThumb() pulls the thumbnail strip along to keep it in view */
    function galleryStep(dir) {
      var cur = Math.round(grid.scrollLeft / cellW());
      goToIndex(cur + dir);
    }
    if (prevBtn) prevBtn.addEventListener('click', function () { galleryStep(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { galleryStep(1); });

    if (lightboxOn) {
      each(cells, function (cell, i) {
        if (cell.getAttribute('data-np-media-type') !== 'image') return; // video/3D play inline, no static viewer for them
        cell.addEventListener('click', function () { openViewer(i); });
      });
    }

    /* ---- fullscreen viewer (built once, on <body>) — images only; video/3D
       media has no data-np-full and is skipped, so vImgs/vThumbs stay sparse
       and keyed by the same index as `cells` (no reindexing needed). ---- */
    var viewer, vScroll, vImgs = [], vThumbs = [];
    var viewerId = 'np-viewer--' + root.id;

    function buildViewer() {
      var old = document.getElementById(viewerId);
      if (old) old.remove();
      viewer = document.createElement('div');
      viewer.id = viewerId;
      viewer.className = 'np-viewer';
      viewer.innerHTML =
        '<div class="np-viewer__scroll" data-s></div>' +
        '<div class="np-viewer__rail" data-r></div>' +
        '<button type="button" class="np-viewer__close" aria-label="Close">' +
          '<svg viewBox="0 0 16 16" fill="none"><path d="M1 1l14 14M15 1L1 15" stroke="currentColor" stroke-width="1.5"/></svg>' +
        '</button>';
      vScroll = viewer.querySelector('[data-s]');
      var vRail = viewer.querySelector('[data-r]');
      vImgs = []; vThumbs = [];
      each(cells, function (cell, i) {
        var full = cell.getAttribute('data-np-full');
        if (!full) return; // video/3D slide — not part of the static fullscreen viewer
        var srcImg = cell.querySelector('img');
        var im = document.createElement('img');
        im.className = 'np-viewer__img'; im.decoding = 'async';
        im.loading = i < 2 ? 'eager' : 'lazy';
        var w = srcImg && srcImg.getAttribute('width');
        var h = srcImg && srcImg.getAttribute('height');
        if (w && h) { im.setAttribute('width', w); im.setAttribute('height', h); } // reserve height pre-load
        im.src = full; im.alt = srcImg ? srcImg.alt : '';
        vScroll.appendChild(im); vImgs[i] = im;
        var tb = document.createElement('button');
        tb.type = 'button'; tb.className = 'np-viewer__thumb';
        var ti = document.createElement('img');
        ti.loading = 'lazy'; ti.alt = '';
        ti.src = srcImg ? (srcImg.currentSrc || srcImg.src) : full;
        tb.appendChild(ti); vRail.appendChild(tb); vThumbs[i] = tb;
        tb.addEventListener('click', function () { vImgs[i].scrollIntoView({ behavior: 'smooth', block: 'start' }); });
      });
      viewer.querySelector('.np-viewer__close').addEventListener('click', closeViewer);
      document.body.appendChild(viewer);

      /* scroll-spy: whichever image sits nearest the top of the viewport wins */
      var spyRaf;
      function spy() {
        var vh = vScroll.clientHeight;
        var best = -1, bestDist = Infinity;
        each(vImgs, function (img, i) {
          if (!img) return;
          var r = img.getBoundingClientRect();
          if (r.bottom <= 0 || r.top >= vh) return;
          var dist = Math.abs(r.top);
          if (dist < bestDist) { bestDist = dist; best = i; }
        });
        if (best === -1) return;
        each(vThumbs, function (t, n) { if (t) t.classList.toggle('is-active', n === best); });
        var at = vThumbs[best], rail = at && at.parentElement;
        if (at && rail) {
          var top = at.offsetTop, bot = top + at.offsetHeight;
          if (top < rail.scrollTop || bot > rail.scrollTop + rail.clientHeight) {
            rail.scrollTop = top - rail.clientHeight / 2 + at.offsetHeight / 2;
          }
        }
      }
      vScroll.addEventListener('scroll', function () {
        if (spyRaf) cancelAnimationFrame(spyRaf);
        spyRaf = requestAnimationFrame(spy);
      }, { passive: true });
      viewer._npSpy = spy;
    }

    var viewerOpen = false;
    function openViewer(i) {
      if (!viewer || !viewer.isConnected) buildViewer();
      if (!vImgs[i]) return; // no fullscreen slide for this index (video/3D)
      viewer.classList.add('is-open');
      if (!viewerOpen) { viewerOpen = true; lockScroll(); }
      window.__npActiveViewerClose = closeViewer;
      requestAnimationFrame(function () {
        if (vImgs[i]) vScroll.scrollTop = vImgs[i].offsetTop; // instant jump, no smooth
        if (viewer._npSpy) viewer._npSpy();
      });
    }
    function closeViewer() {
      if (!viewer) return;
      viewer.classList.remove('is-open');
      if (viewerOpen) { viewerOpen = false; unlockScroll(); }
      if (window.__npActiveViewerClose === closeViewer) window.__npActiveViewerClose = null;
    }
    /* Escape is bound once per PAGE, not once per gallery init — see the
       guarded listener in BOOTSTRAP below, which closes whichever viewer
       instance is currently open via window.__npActiveViewerClose. */

    /* jump gallery to a media index (used on colour change) */
    root._npGalleryGo = function (index) {
      if (index == null || index < 0 || index >= cells.length) return;
      if (mq.matches) goToIndex(index);
      else if (cells[index]) cells[index].scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    markThumb(0);
  }

  /* ======================================================================
     OPTIONS -> VARIANT
     ====================================================================== */
  function initOptions(root, cfg, variants) {
    if (!variants || !variants.length) return;

    var idInput = root.querySelector('[data-np-id]');
    var priceEl = root.querySelector('[data-np-price]');
    var submit = root.querySelector('[data-np-submit]');
    var submitText = root.querySelector('[data-np-submit-text]');
    var stockEl = root.querySelector('[data-np-stock]');
    var swatchWrap = root.querySelector('[data-np-swatches]');
    var swatchName = root.querySelector('[data-np-swatch-name]');
    var sizeWrap = root.querySelector('[data-np-sizes]');

    var LABEL_ADD = submit ? (submit.getAttribute('data-label-add') || (submitText && submitText.textContent.trim()) || 'Add to cart') : 'Add to cart';
    var LABEL_SOLD = submit ? (submit.getAttribute('data-label-sold') || 'Sold out') : 'Sold out';
    var LABEL_UNAVAIL = submit ? (submit.getAttribute('data-label-unavail') || 'Unavailable') : 'Unavailable';
    var LABEL_SELECT = submit ? (submit.getAttribute('data-label-select') || '') : '';

    var colorPos = cfg.optionColor || 0; // 1-based; 0 = no colour option
    var sizePos = cfg.optionSize || 0;
    var hasSize = sizePos > 0;

    // a size is "chosen" only after the shopper clicks a pill; before that the
    // ATC shows "Select a size". (No size option, or no select-label -> always ready.)
    var sizeChosen = !hasSize || LABEL_SELECT === '';

    function pickInitial() {
      var m = location.search.match(/[?&]variant=(\d+)/);
      if (m) {
        var byId = variants.filter(function (v) { return String(v.id) === m[1]; })[0];
        if (byId) return byId;
      }
      if (cfg.optionNames && cfg.optionNames.length) {
        var params = new URLSearchParams(location.search);
        var wanted = cfg.optionNames.map(function (name) { return params.get(name); });
        if (wanted.some(function (v) { return v != null; })) {
          var byOpts = variants.filter(function (v) {
            for (var i = 0; i < wanted.length; i++) {
              if (wanted[i] != null && v.options[i] !== wanted[i]) return false;
            }
            return true;
          })[0];
          if (byOpts) return byOpts;
        }
      }
      return variants.filter(function (v) { return v.available; })[0] || variants[0];
    }
    function findExact(opts) {
      return variants.filter(function (v) {
        for (var i = 0; i < opts.length; i++) { if (v.options[i] !== opts[i]) return false; }
        return true;
      })[0];
    }

    var current = pickInitial();
    var sel = current.options.slice();
    var booted = false; // suppress gallery auto-jump on first apply()

    function money(cents) { return formatMoney(cents, cfg.moneyFormat); }

    function apply(v) {
      /* swatch state */
      if (colorPos && swatchWrap) {
        var cval = sel[colorPos - 1];
        if (swatchName) swatchName.textContent = cval || '';
        each(swatchWrap.querySelectorAll('[data-np-swatch]'), function (a) {
          a.classList.toggle('is-selected', a.getAttribute('data-value') === cval);
        });
      }
      /* size availability for the current colour */
      if (sizePos && sizeWrap) {
        each(sizeWrap.querySelectorAll('[data-np-size-pill]'), function (pill) {
          var sval = pill.getAttribute('data-value');
          var probe = sel.slice(); probe[sizePos - 1] = sval;
          var pv = findExact(probe);
          var ok = pv && pv.available;
          pill.classList.toggle('is-soldout', !ok);
          var inp = pill.querySelector('[data-np-size-input]');
          if (inp) {
            inp.disabled = !pv;
            inp.checked = sizeChosen && (sval === sel[sizePos - 1]);
          }
        });
      }
      /* no variant for this combo */
      if (!v) {
        if (submit) submit.setAttribute('disabled', 'disabled');
        if (submitText) submitText.textContent = LABEL_UNAVAIL;
        return;
      }
      if (idInput) idInput.value = v.id;
      if (priceEl) priceEl.innerHTML = money(v.price);
      if (submit) {
        if (hasSize && !sizeChosen) {
          submit.setAttribute('disabled', 'disabled');
          submit.classList.add('is-select');
          if (submitText) submitText.textContent = LABEL_SELECT;
        } else if (v.available) {
          submit.classList.remove('is-select');
          submit.removeAttribute('disabled');
          if (submitText) submitText.textContent = LABEL_ADD;
        } else {
          submit.classList.remove('is-select');
          submit.setAttribute('disabled', 'disabled');
          if (submitText) submitText.textContent = LABEL_SOLD;
        }
      }
      /* stock note */
      if (stockEl) {
        if (hasSize && !sizeChosen) {
          stockEl.hidden = true;
        } else {
          var thr = parseInt(stockEl.getAttribute('data-threshold'), 10) || 0;
          var low = stockEl.getAttribute('data-low') || '';
          var out = stockEl.getAttribute('data-out') || '';
          var qty = (typeof v.inventory_quantity === 'number') ? v.inventory_quantity : null;
          var tracked = !!v.inventory_management;
          if (!v.available) { stockEl.textContent = out; stockEl.hidden = false; }
          else if (tracked && qty !== null && qty > 0 && qty <= thr) { stockEl.textContent = low; stockEl.hidden = false; }
          else { stockEl.hidden = true; }
        }
      }
      /* url — only once the shopper actually changes something; on first
         load the URL already matches whatever they landed on */
      if (booted) {
        try {
          var u = new URL(location.href);
          u.searchParams.set('variant', v.id);
          history.replaceState({}, '', u.toString());
        } catch (e) {}
      }
      /* gallery jump to the variant's media (not on first load) */
      if (booted && v.featured_image && v.featured_image.position && typeof root._npGalleryGo === 'function') {
        root._npGalleryGo(v.featured_image.position - 1);
      }
    }

    function setOption(pos, value) {
      sel[pos - 1] = value;
      var v = findExact(sel);
      if (!v) {
        var keep = variants.filter(function (x) { return x.options[pos - 1] === value && x.available; })[0]
                || variants.filter(function (x) { return x.options[pos - 1] === value; })[0];
        if (keep) { sel = keep.options.slice(); v = keep; }
      }
      apply(v);
    }

    if (colorPos && swatchWrap) {
      each(swatchWrap.querySelectorAll('[data-np-swatch]'), function (a) {
        a.addEventListener('click', function (e) {
          e.preventDefault();
          setOption(colorPos, a.getAttribute('data-value'));
        });
        a.addEventListener('mouseenter', function () {
          if (swatchName) swatchName.textContent = a.getAttribute('data-value');
        });
        a.addEventListener('mouseleave', function () {
          if (swatchName) swatchName.textContent = sel[colorPos - 1] || '';
        });
      });
    }
    if (sizePos && sizeWrap) {
      each(sizeWrap.querySelectorAll('[data-np-size-input]'), function (inp) {
        inp.addEventListener('change', function () {
          if (inp.disabled) return;
          sizeChosen = true;
          setOption(sizePos, inp.value);
        });
      });
    }

    apply(current);
    booted = true;
  }

  /* ======================================================================
     DRAWERS  (one shared right-slide panel per section, mounted on <body>)
     ====================================================================== */
  function initDrawers(root) {
    var src = root.querySelector('[data-np-drawer-panel]');
    var rows = root.querySelectorAll('[data-np-drawer-open]');
    if (!rows.length) { if (src) src.remove(); return; }

    var panelId = 'np-drawer-mount--' + root.id;
    var panel = document.getElementById(panelId);

    if (!panel && src) {
      panel = document.createElement('div');
      panel.id = panelId;
      panel.className = 'np-drawer-mount';
      while (src.firstChild) panel.appendChild(src.firstChild);
      document.body.appendChild(panel);
    }
    if (src) src.remove();
    if (!panel) return;

    var overlay = panel.querySelector('[data-np-drawer-overlay]');
    var aside = panel.querySelector('.np-drawer');
    var titleEl = panel.querySelector('[data-np-drawer-title]');
    var bodyEl = panel.querySelector('[data-np-drawer-body]');
    var closeBtn = panel.querySelector('[data-np-drawer-close]');
    if (!aside) return;

    // open-state lives on the panel (survives shopify:section:load re-inits, so
    // every closure — rows, ✕, overlay, Esc, drag — agrees and lockScroll stays balanced)
    function openDrawer(title, html) {
      if (titleEl) titleEl.textContent = title || '';
      if (bodyEl) { bodyEl.innerHTML = html || ''; bodyEl.scrollTop = 0; }
      aside.style.transform = '';                 // drop any leftover drag offset
      if (overlay) overlay.classList.add('is-open');
      aside.classList.add('is-open');
      if (!panel._npOpen) { panel._npOpen = true; lockScroll(); }
    }
    function closeDrawer() {
      if (overlay) overlay.classList.remove('is-open');
      aside.classList.remove('is-open');
      aside.style.transform = '';                 // hand the slide-out back to the CSS class
      if (panel._npOpen) { panel._npOpen = false; unlockScroll(); }
    }

    each(rows, function (btn) {
      btn.addEventListener('click', function () {
        var store = document.getElementById(btn.getAttribute('data-target'));
        openDrawer(btn.getAttribute('data-title'), store ? store.innerHTML : '');
      });
    });

    if (!panel.dataset.npBound) {
      panel.dataset.npBound = '1';
      if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
      if (overlay) overlay.addEventListener('click', closeDrawer);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && aside.classList.contains('is-open')) closeDrawer();
      });

      /* mobile bottom-sheet: drag the header down to dismiss.
         Position writes are batched through requestAnimationFrame so the
         drag can't outrun paint, and downward motion gets a soft cushion
         past 90px instead of tracking the finger 1:1 (which read as a
         stiff, mechanical drag rather than a soft, air-resisted one). */
      var head = panel.querySelector('.np-drawer__head');
      var sheetMq = window.matchMedia('(max-width: 749px)');
      var dActive = false, dStartY = 0, dDY = 0, dH = 1, dRaf = null;
      function dStart(e) {
        if (!sheetMq.matches || !aside.classList.contains('is-open')) return;
        var t = e.touches ? e.touches[0] : e;
        dActive = true; dStartY = t.clientY; dDY = 0;
        dH = aside.getBoundingClientRect().height || 1;
        aside.classList.add('is-dragging');
      }
      function dPaint() {
        dRaf = null;
        aside.style.transform = 'translateY(' + dDY + 'px)';
      }
      function dMove(e) {
        if (!dActive) return;
        var t = e.touches ? e.touches[0] : e;
        var raw = t.clientY - dStartY;
        if (raw < 0) dDY = raw * 0.25;             // resist dragging upward
        else dDY = raw < 90 ? raw : 90 + (raw - 90) * 0.4;  // soft cushion nearing dismiss
        if (e.cancelable) e.preventDefault();
        if (!dRaf) dRaf = requestAnimationFrame(dPaint);
      }
      function dEnd() {
        if (!dActive) return;
        dActive = false;
        if (dRaf) { cancelAnimationFrame(dRaf); dRaf = null; }
        aside.classList.remove('is-dragging');
        if (dDY > dH * 0.28 || dDY > 130) closeDrawer();  // dismiss
        else aside.style.transform = '';                  // snap back up
      }
      if (head) {
        head.addEventListener('touchstart', dStart, { passive: true });
        head.addEventListener('touchmove', dMove, { passive: false });
        head.addEventListener('touchend', dEnd);
        head.addEventListener('touchcancel', dEnd);
      }
    }
  }

  /* ======================================================================
     BUY — AJAX add-to-cart into this theme's own <new-cart-drawer>.
     The button lives inside a plain {% form 'product' %} (not the stock
     <product-form> element, which only knows about <cart-drawer> /
     <cart-notification> and would otherwise hard-navigate to /cart). We
     intercept the submit, post to cart/add.js, hand the response straight
     to the drawer's own applySections()/open(), then auto-close it after
     the merchant's configured delay — same drawer, same transition, same
     timing as every other add-to-cart entry point on the site.
     ====================================================================== */
  function initBuy(root) {
    var wrap = root.querySelector('[data-np-buy-form]');
    if (!wrap) return;
    var form = wrap.querySelector('form');
    var submit = wrap.querySelector('[data-np-submit]');
    if (!form || !submit) return;

    var spinner = submit.querySelector('.loading__spinner');
    var errorWrap = wrap.querySelector('.np-buy__error');
    var errorText = errorWrap ? errorWrap.querySelector('.product-form__error-message') : null;
    var closeDelay = parseFloat(wrap.getAttribute('data-close-delay'));
    if (isNaN(closeDelay)) closeDelay = 0;
    var closeTimer;

    function shopRoot() {
      return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    }
    function showError(msg) {
      if (!errorWrap) return;
      if (errorText) errorText.textContent = msg || '';
      errorWrap.hidden = !msg;
    }

    form.addEventListener('submit', function (evt) {
      evt.preventDefault();
      if (submit.classList.contains('loading')) return;
      if (submit.hasAttribute('disabled')) return;

      showError('');
      submit.classList.add('loading');
      submit.setAttribute('aria-disabled', 'true');
      if (spinner) spinner.classList.remove('hidden');

      var drawer = document.querySelector('new-cart-drawer');
      var sectionId = drawer ? drawer.sectionId : 'new-cart-drawer';
      var idInput = form.querySelector('[name="id"]');

      fetch(shopRoot() + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: idInput ? idInput.value : undefined,
          quantity: 1,
          sections: [sectionId, 'cart-icon-bubble'],
          sections_url: window.location.pathname
        })
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (!res.ok || res.data.status) throw res.data;

          if (drawer && typeof drawer.applySections === 'function') drawer.applySections(res.data.sections);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'new-product',
              productVariantId: idInput ? idInput.value : null,
              cartData: res.data
            });
          }
          document.dispatchEvent(new CustomEvent('new-cart:updated', { bubbles: true, detail: res.data }));

          if (drawer && typeof drawer.open === 'function') {
            drawer.open();
            if (closeTimer) clearTimeout(closeTimer);
            if (closeDelay > 0) {
              closeTimer = setTimeout(function () {
                if (typeof drawer.close === 'function') drawer.close();
              }, closeDelay * 1000);
            }
          } else {
            window.location = shopRoot() + 'cart';
          }
        })
        .catch(function (err) {
          showError((err && (err.description || err.message)) || '');
        })
        .finally(function () {
          submit.classList.remove('loading');
          submit.removeAttribute('aria-disabled');
          if (spinner) spinner.classList.add('hidden');
        });
    });
  }

  /* ======================================================================
     STICKY RAIL — pin it below the site's sticky header, not under it
     ====================================================================== */
  function initRail(root, cfg) {
    var rail = root.querySelector('.np__rail');
    if (!rail) return;
    var gap = (typeof cfg.stickyGap === 'number') ? cfg.stickyGap : 16;

    function stuckHeaderHeight() {
      var sels = ['sticky-header', '.header-wrapper', '.section-header', '#shopify-section-header', '.shopify-section-group-header-group', 'header[role="banner"]'];
      var h = 0;
      for (var i = 0; i < sels.length; i++) {
        var el = document.querySelector(sels[i]);
        if (!el) continue;
        var r = el.getBoundingClientRect();
        if (r.height < 10 || r.height > 260) continue;
        // only count it if it's actually pinned to the top
        var inner = el.querySelector('*') || el;
        var pos = getComputedStyle(el).position;
        var posInner = getComputedStyle(inner).position;
        if (pos === 'sticky' || pos === 'fixed' || posInner === 'sticky' || posInner === 'fixed') {
          h = Math.max(h, r.height);
        }
      }
      return h;
    }

    function apply() {
      root.style.setProperty('--np-sticky', (stuckHeaderHeight() + gap) + 'px');
    }
    apply();
    var t;
    window.addEventListener('resize', function () { clearTimeout(t); t = setTimeout(apply, 150); });
    window.addEventListener('load', apply);
  }

  /* ======================================================================
     SIZE-CHART BUTTON  (MP Size Chart app block relocated inline with the
     "Size" label — .np-sizes__head is already flex + space-between, so the
     app's own button just needs to become its second child)
     The app hydrates its button async (mageplaza_sc goes _mpLoading ->
     _mpLoaded after the block already exists in the DOM), so a single
     check at init misses it — watch the rail until it shows up.
     ====================================================================== */
  function initSizeChartButton(root) {
    var head = root.querySelector('.np-sizes__head');
    if (!head) return;

    function relocate() {
      var blocks = root.querySelectorAll('.np__block--app');
      for (var i = 0; i < blocks.length; i++) {
        if (blocks[i].querySelector('[id^="Avada-SC"]')) {
          head.appendChild(blocks[i]);
          return true;
        }
      }
      return false;
    }

    if (relocate()) return;

    var rail = root.querySelector('[data-np-rail]') || root;
    var obs = new MutationObserver(function () {
      if (relocate()) obs.disconnect();
    });
    obs.observe(rail, { childList: true, subtree: true });
    setTimeout(function () { obs.disconnect(); }, 15000);
  }

  /* ======================================================================
     BOOTSTRAP
     ====================================================================== */
  function initSection(root) {
    if (!root || root.dataset.npReady === '1') return;
    root.dataset.npReady = '1';
    var cfg = readJSON(root.querySelector('[data-np-config]')) || {};
    var variants = readJSON(root.querySelector('[data-np-variants]')) || [];
    var invMap = readJSON(root.querySelector('[data-np-inventory]')) || {};
    each(variants, function (v) {
      if (!('inventory_quantity' in v) && v.id in invMap) v.inventory_quantity = invMap[v.id];
    });
    try { initRail(root, cfg); } catch (e) { if (window.console) console.warn('[new-product] rail', e); }
    try { initGallery(root, cfg); } catch (e) { if (window.console) console.warn('[new-product] gallery', e); }
    try { initOptions(root, cfg, variants); } catch (e) { if (window.console) console.warn('[new-product] options', e); }
    try { initDrawers(root); } catch (e) { if (window.console) console.warn('[new-product] drawers', e); }
    try { initSizeChartButton(root); } catch (e) { if (window.console) console.warn('[new-product] size-chart button', e); }
    try { initBuy(root); } catch (e) { if (window.console) console.warn('[new-product] buy', e); }
  }

  function boot(scope) {
    each((scope || document).querySelectorAll('[id^="NewProd-"]'), initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }
  /* the <script src> tag lives inside the section markup, so Shopify re-runs
     this whole file every time a merchant edits a setting in the Customizer.
     boot() itself is safe to re-run (data-np-ready guards each root), but a
     plain document.addEventListener here would stack a fresh duplicate
     listener on every edit — this flag makes sure each one is only ever
     bound once per page. */
  if (!window.__npSectionLoadBound) {
    window.__npSectionLoadBound = true;
    document.addEventListener('shopify:section:load', function (e) {
      if (e.target && e.target.id && e.target.id.indexOf('NewProd-') === 0) initSection(e.target);
      else boot(e.target || document);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && window.__npActiveViewerClose) window.__npActiveViewerClose();
    });
  }
})();
